/**
 * TokenomicsCards — 4 illustrated cards summarising the supply allocation.
 *
 * Sprint 17.A.2 — UX refresh:
 *   - Top text block has a uniform fixed min-height so all 4 illustrations
 *     start at exactly the same Y across cards (was previously misaligned
 *     because the "Public" card carries longer status copy).
 *   - Each card animates in via Framer Motion (whileInView, staggered).
 *   - Each card emits a soft coloured glow matching its dominant tone
 *     (cyan for Public, gold for Treasury, indigo for Shadows, ember red
 *     for Burn). The glow intensifies on hover.
 *   - Section header now follows the "Allocation & Discipline" pattern
 *     (mono kicker + font-display title) used across the landing page.
 *
 * Layout (per card):
 *
 *   ┌─────────────────────────┐
 *   │  ΔΣ-XX kicker           │
 *   │  Title                  │  ← top, fixed min-height for alignment
 *   │  55%   STATE_CHIP       │
 *   │  Lock proof link?       │
 *   ├─────────────────────────┤  ← ALWAYS at the same Y across cards
 *   │   AI illustration       │     (16:10 aspect ratio)
 *   │   (full-bleed)          │
 *   └─────────────────────────┘
 *
 * Hero images come from /api/assets/tokenomics_<id>.jpg (committed JPGs
 * generated by backend/scripts/generate_email_asset.py --all-tokenomics
 * via gpt-image-1, ~30-50 KB each, 960×540 16:9 already-cropped).
 *
 * Card 4 (Burn) is a hybrid: top section reads from the burn aggregate
 * /api/treasury/burns; the bottom illustration is static. Empty state
 * shows a placeholder copy ("Phase 1 active · First burn at J+30").
 */

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock,
  ExternalLink,
  Users,
  Vault,
  Eye,
  Flame,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { URLS } from "@/lib/launchPhase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOTAL_SUPPLY = 1_000_000_000;

// ---------------------------------------------------------------------
// Burn data fetch
// ---------------------------------------------------------------------
interface BurnSummary {
  total_burned_tokens: number;
  burn_count: number;
  last_burn_at: string | null;
  last_burn_signature: string | null;
}

function formatBurnPercent(burned: number): string {
  if (!burned) return "0%";
  const pct = (burned / TOTAL_SUPPLY) * 100;
  if (pct < 0.01) return "<0.01%";
  if (pct < 1) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(1)}%`;
}

// ---------------------------------------------------------------------
// Card model
// ---------------------------------------------------------------------
type LockState = "open" | "locked" | "pending";

interface CardConfig {
  id: "public" | "treasury" | "shadows" | "burn";
  /** ΔΣ-XX dossier code shown as kicker. */
  code: string;
  /** Image filename (without extension). Served from /api/assets. */
  imageSlug: string;
  /** Lucide icon shown next to the title. */
  // eslint-disable-next-line
  Icon: any;
  /** Dominant glow colour for this card. */
  glow: string;
  /** Resolves the right-side data slot at render time. */
  metric: () => {
    valueLabel: string; // big number (% or token count)
    state: LockState;
    stateText: string;
    lockUrl?: string;
  };
}

// ---------------------------------------------------------------------
// State chip
// ---------------------------------------------------------------------
const StateChip: React.FC<{ state: LockState; label: string }> = ({
  state,
  label,
}) => {
  const styles =
    state === "locked"
      ? "border-[#33FF33]/40 bg-[#33FF33]/10 text-[#33FF33]"
      : state === "pending"
        ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"
        : "border-foreground/15 bg-foreground/5 text-foreground/70";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-[0.2em] border ${styles}`}
    >
      {state !== "open" && <Lock size={9} />}
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------
// Illustrated card
// ---------------------------------------------------------------------
interface IllustratedCardProps {
  cfg: CardConfig;
  i18nKey: string;
  index: number;
}

const IllustratedCard: React.FC<IllustratedCardProps> = ({
  cfg,
  i18nKey,
  index,
}) => {
  const { t } = useI18n();
  const m = cfg.metric();
  const heroUrl = `${BACKEND_URL}/api/assets/${cfg.imageSlug}.jpg`;
  const Icon = cfg.Icon;

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{
        duration: 0.55,
        delay: index * 0.08,
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={{ y: -4 }}
      className="card-glow group relative flex flex-col rounded-xl border border-foreground/15 bg-foreground/[0.03] overflow-hidden"
      data-testid={`tokenomics-card-${cfg.id}`}
      data-card-state={m.state}
      style={
        {
          // Fed into CSS via custom property so :hover can amplify the glow
          // smoothly without re-rendering React.
          "--card-glow": cfg.glow,
        } as React.CSSProperties
      }
    >
      {/* ---- Top slab — uniform fixed height so images align across cards ---- */}
      <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-4 flex flex-col gap-2 h-[180px] shrink-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-foreground/55">
            {cfg.code}
          </span>
          <Icon size={14} className="text-foreground/55" />
        </div>
        <h3
          className="text-[12px] sm:text-[13px] font-semibold tracking-wide leading-snug text-foreground"
          data-testid={`tokenomics-card-${cfg.id}-title`}
        >
          {t(`tokenomics.cards.${i18nKey}.title`) as string}
        </h3>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="font-mono text-2xl sm:text-3xl font-semibold tabular text-foreground"
            data-testid={`tokenomics-card-${cfg.id}-value`}
          >
            {m.valueLabel}
          </span>
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 flex-wrap">
          <StateChip state={m.state} label={m.stateText} />
          {m.lockUrl && (
            <a
              href={m.lockUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.2em] text-[#2DD4BF] hover:underline"
              data-testid={`tokenomics-card-${cfg.id}-lock-link`}
            >
              Proof <ExternalLink size={9} />
            </a>
          )}
        </div>
      </div>

      {/* ---- Bottom slab — illustration (always starts at the same Y) ---- */}
      <div
        className="relative aspect-[16/10] overflow-hidden border-t border-foreground/10 bg-black mt-auto"
        data-testid={`tokenomics-card-${cfg.id}-image`}
      >
        <img
          src={heroUrl}
          alt={t(`tokenomics.cards.${i18nKey}.alt`) as string}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.045]"
        />
        {/* Bottom gradient — readability anchor for any overlay */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/55 to-transparent pointer-events-none"
        />
        {/* Coloured top edge — subtle dominant accent */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-[2px] opacity-60 group-hover:opacity-100 transition-opacity"
          style={{ background: cfg.glow }}
        />
      </div>
    </motion.article>
  );
};

// ---------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------
export function TokenomicsCards() {
  const { t } = useI18n();
  const [burn, setBurn] = useState<BurnSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get<BurnSummary>(`${API}/treasury/burns`);
        if (!cancelled) setBurn(res.data);
      } catch {
        if (!cancelled) {
          setBurn({
            total_burned_tokens: 0,
            burn_count: 0,
            last_burn_at: null,
            last_burn_signature: null,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const burnReady = burn !== null;
  const burned = burn?.total_burned_tokens || 0;

  const cards: CardConfig[] = [
    {
      id: "public",
      code: "ΔΣ-55",
      imageSlug: "tokenomics_public",
      Icon: Users,
      // Cyan/teal — dominant tone of the dystopian crowd illustration.
      glow: "rgba(45, 212, 191, 0.55)",
      metric: () => ({
        valueLabel: "55%",
        state: "open",
        stateText: t("tokenomics.cards.public.terms") as string,
      }),
    },
    {
      id: "treasury",
      code: "ΔΣ-30",
      imageSlug: "tokenomics_treasury",
      Icon: Vault,
      // Amber/gold — the dial wheel + ΔΣ monogram.
      glow: "rgba(245, 158, 11, 0.55)",
      metric: () => {
        const url = URLS.treasuryLock;
        return {
          valueLabel: "30%",
          state: url ? "locked" : "pending",
          stateText: url
            ? (t("tokenomics.cards.treasury.locked") as string)
            : (t("tokenomics.cards.lockPending") as string),
          lockUrl: url || undefined,
        };
      },
    },
    {
      id: "shadows",
      code: "ΔΣ-15",
      imageSlug: "tokenomics_shadows",
      Icon: Eye,
      // Indigo/violet — long noir corridor with cold sconce.
      glow: "rgba(129, 140, 248, 0.5)",
      metric: () => {
        const url = URLS.teamLock;
        return {
          valueLabel: "15%",
          state: url ? "locked" : "pending",
          stateText: url
            ? (t("tokenomics.cards.shadows.locked") as string)
            : (t("tokenomics.cards.lockPending") as string),
          lockUrl: url || undefined,
        };
      },
    },
    {
      id: "burn",
      code: "ΔΣ-FIRE",
      imageSlug: "tokenomics_burn",
      Icon: Flame,
      // Ember red/orange — the ritual flame.
      glow: "rgba(239, 68, 68, 0.6)",
      metric: () => {
        if (!burnReady) {
          return {
            valueLabel: "—",
            state: "open" as LockState,
            stateText: t("common.loading") as string,
          };
        }
        if (burned === 0) {
          return {
            valueLabel: "0%",
            state: "open" as LockState,
            stateText: t("tokenomics.cards.burn.empty") as string,
          };
        }
        return {
          valueLabel: formatBurnPercent(burned),
          state: "open" as LockState,
          stateText: `${burned.toLocaleString()} ${t("tokenomics.cards.burn.tokens") as string}`,
        };
      },
    },
  ];

  return (
    <div
      className="mt-16 pt-12 border-t border-border/60"
      data-testid="tokenomics-cards"
    >
      {/* ---- Section header — same typographic system as Tokenomics title ---- */}
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
          {t("tokenomics.cards.sectionKicker") as string}
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mt-2">
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight">
            {t("tokenomics.cards.sectionTitle") as string}
          </h3>
          {burnReady && burn?.last_burn_at && burn?.last_burn_signature && (
            <a
              href={URLS.solscanTx(burn.last_burn_signature)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/55 hover:text-foreground transition-colors self-start md:self-end"
            >
              🔥 {t("tokenomics.cards.burn.lastBurn") as string}:{" "}
              {new Date(burn.last_burn_at).toISOString().slice(0, 10)}
            </a>
          )}
        </div>
        <p className="mt-3 text-foreground/75 leading-relaxed max-w-2xl text-sm md:text-[15px]">
          {t("tokenomics.cards.sectionSubtitle") as string}
        </p>
      </div>

      {/* ---- 4 cards grid ---- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {cards.map((c, i) => (
          <IllustratedCard key={c.id} cfg={c} i18nKey={c.id} index={i} />
        ))}
      </div>

      {/* ---- Cross-link to /transparency ---- */}
      <div className="mt-8 text-center">
        <Link
          to="/transparency"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground transition-colors"
          data-testid="tokenomics-cards-transparency-link"
        >
          {t("tokenomics.cards.transparencyLink") as string}
          <ExternalLink size={11} />
        </Link>
      </div>
    </div>
  );
}

export default TokenomicsCards;
